import { graphql } from "gatsby"
import Img from "gatsby-image"
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from "gatsby-source-contentful/rich-text"
import React, { useContext } from "react"
import { css } from "styled-components"
import { TContentfulImage } from "../atoms/breakpoint-image"
import { T } from "../atoms/text"
import VariantContainer from "../atoms/variant-container"
import { BackgroundColourContext } from "../util/background-colour-context"
import ContentBlock from "./content-block"

export const USE_CASE_CARD_TYPENAME = "ContentfulUseCaseCard"

export interface IUseCaseCard {
  image: TContentfulImage
  __typename: typeof USE_CASE_CARD_TYPENAME
  title?: string
  body: RenderRichTextData<ContentfulRichTextGatsbyReference> | string
}

const UseCaseCard = ({
  image,
  title,
  body,
}: Omit<IUseCaseCard, "__typename">) => {
  const {
    state: { dark },
  } = useContext(BackgroundColourContext)
  return (
    <VariantContainer
      variants={[
        css`
          display: flex;
          flex-direction: column;
          background-color: ${dark ? `#303030` : `var(--white)`};
        `,
        null,
        css`
          :nth-child(2n + 1) {
            flex-direction: row;
          }
          :nth-child(2n) {
            flex-direction: row-reverse;
          }
          :not(:first-child) {
            margin-top: 32px;
          }
          max-width: 1085px;
          box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.07);
          border-radius: 2px;
        `,
      ]}
    >
      {image?.fluid && (
        <VariantContainer
          variants={[
            null,
            css`
              margin: 32px 32px 0px 32px;
            `,
            css`
              margin: 32px 48px;
              width: 41.47%;
            `,
          ]}
        >
          <Img fluid={image.fluid} />
        </VariantContainer>
      )}
      <VariantContainer
        variants={[
          css`
            padding: 24px 28px;
          `,
          css`
            padding: 32px;
          `,
          css`
            padding: 0 48px;
            align-self: center;
            width: 40.83%;
            text-align: left;
          `,
        ]}
      >
        {typeof body === "string" ? (
          <ContentBlock
            title={title}
            titleVariants={[
              css`
                text-align: center;
                ${T.sm.h4`500`};
                color: ${dark ? `var(--white)` : `var(--slate)`};
              `,
              css`
                ${T.md.h4`500`};
              `,
              css`
                ${T.lg.h4`500`};
                text-align: left;
              `,
            ]}
            description={body}
            bodyVariants={[
              css`
                text-align: center;
                margin-top: 16px;
                color: ${dark ? `var(--snow)` : `var(--body-text-grey)`};
              `,
              null,
              css`
                text-align: left;
              `,
            ]}
          />
        ) : (
          <ContentBlock.RichText>{body}</ContentBlock.RichText>
        )}
      </VariantContainer>
    </VariantContainer>
  )
}

export const UseCaseCardFragment = graphql`
  fragment ContentfulUseCaseCard on ContentfulUseCaseCard {
    __typename
    image {
      fluid(quality: 90) {
        ...GatsbyContentfulFluid_withWebp
      }
    }
    body {
      raw
    }
  }
`
export default UseCaseCard
